"use client";

import {
  Cloud,
  Server,
  Database,
  Smartphone,
  Globe,
  Code,
  Building2,
  Layers,
  Megaphone,
  TrendingUp,
  BarChart3,
  ShoppingCart,
  CreditCard,
  ShieldCheck,
  Bug,
  Brain,
  Cpu,
  Zap,
  type LucideIcon,
} from "lucide-react";

const categoryIcons: Record<string, LucideIcon[]> = {
  "cloud-devops": [Cloud, Server, Database],
  "cloud-and-devops": [Cloud, Server, Database],
  "mobile-apps-development": [Smartphone, Code, Layers],
  "web-app-development": [Globe, Code, Layers],
  "custom-enterprise-apps": [Building2, Layers, Database],
  "digital-marketing": [Megaphone, TrendingUp, BarChart3],
  "e-commerce-website": [ShoppingCart, CreditCard, Globe],
  "software-testing-qa": [ShieldCheck, Bug, Code],
  "software-testing-and-qa": [ShieldCheck, Bug, Code],
  "emerging-technologies": [Brain, Cpu, Zap],
};

const categoryGradients: Record<string, [string, string]> = {
  "cloud-devops": ["from-sky-500/20", "to-blue-600/20"],
  "cloud-and-devops": ["from-sky-500/20", "to-blue-600/20"],
  "mobile-apps-development": ["from-violet-500/20", "to-purple-600/20"],
  "web-app-development": ["from-blue-500/20", "to-indigo-600/20"],
  "custom-enterprise-apps": ["from-emerald-500/20", "to-teal-600/20"],
  "digital-marketing": ["from-orange-500/20", "to-red-500/20"],
  "e-commerce-website": ["from-pink-500/20", "to-rose-600/20"],
  "software-testing-qa": ["from-green-500/20", "to-emerald-600/20"],
  "software-testing-and-qa": ["from-green-500/20", "to-emerald-600/20"],
  "emerging-technologies": ["from-cyan-500/20", "to-blue-500/20"],
};

const categoryAccents: Record<string, string> = {
  "cloud-devops": "text-sky-500",
  "cloud-and-devops": "text-sky-500",
  "mobile-apps-development": "text-violet-500",
  "web-app-development": "text-blue-500",
  "custom-enterprise-apps": "text-emerald-500",
  "digital-marketing": "text-orange-500",
  "e-commerce-website": "text-pink-500",
  "software-testing-qa": "text-green-500",
  "software-testing-and-qa": "text-green-500",
  "emerging-technologies": "text-cyan-500",
};

interface ServiceIllustrationProps {
  categorySlug: string;
  className?: string;
}

export function ServiceIllustration({ categorySlug, className }: ServiceIllustrationProps) {
  const icons = categoryIcons[categorySlug] || [Globe, Code, Zap];
  const [from, to] = categoryGradients[categorySlug] || ["from-blue-500/20", "to-violet-600/20"];
  const accent = categoryAccents[categorySlug] || "text-blue-500";

  return (
    <div className={`relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br ${from} ${to} ${className ?? ""}`}>
      {/* Decorative orbs */}
      <div className="absolute -top-12 -right-12 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-primary/5 rounded-full blur-3xl" />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(circle, currentColor 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      {/* Floating icons */}
      <div className="relative h-full flex items-center justify-center gap-6 p-8">
        {icons.map((Icon, i) => (
          <div
            key={i}
            className={`flex items-center justify-center rounded-xl bg-card shadow-lg transition-transform ${
              i === 1 ? "w-16 h-16" : "w-12 h-12"
            }`}
            style={{
              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`,
            }}
          >
            <Icon className={`${accent} ${i === 1 ? "w-8 h-8" : "w-6 h-6"}`} />
          </div>
        ))}
      </div>

      {/* Decorative connecting lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-10" xmlns="http://www.w3.org/2000/svg">
        <line x1="30%" y1="40%" x2="50%" y2="50%" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
        <line x1="50%" y1="50%" x2="70%" y2="40%" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
      </svg>
    </div>
  );
}
